import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mealsApi, summaryApi } from '@/shared/api/endpoints';
import type { DaySummary, Meal, MealItem } from '@/shared/api/types';
import { describeDay, shiftIsoDate, todayIso } from '@/shared/format/dates';
import { mealTypeLabel } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import {
  Banner,
  Button,
  ConfidenceMeter,

  Eyebrow,
  IconButton,
} from '@/shared/ui';
import {
  EsqueletoComidas,
  EsqueletoTotalDelDia,
  useEsqueleto,
} from '@/shared/ui/Skeleton';
import {
  AccountIcon,
  BackIcon,
  CameraIcon,
  EditIcon,
  ForwardIcon,
  TodayIcon,
} from '@/shared/ui/icons';
import { MealCard } from '@/shared/ui/MealCard';
import { DayTotalCard } from './DayTotalCard';
import { ExcludedList } from './ExcludedList';

const longDate = new Intl.DateTimeFormat('es-AR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

export function TodayPage() {
  const { profile } = useSession();
  const navigate = useNavigate();

  const [date, setDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const excludedRef = useRef<HTMLDivElement>(null);

  const summary = useAsync<DaySummary>(() => summaryApi.day(date), [date]);
  const meals = useAsync<Meal[]>(() => mealsApi.forDay(date), [date]);

  const reload = () => {
    summary.reload();
    meals.reload();
  };

  const confirm = async (mealId: string, itemId: string) => {
    setError(null);
    try {
      await mealsApi.confirm(mealId, itemId);
      reload();
    } catch (cause) {
      setError(describeError(cause));
    }
  };

  const esqueletoDelDia = useEsqueleto(summary.loading);
  const isToday = date >= todayIso();
  const needsConfirmation = (meals.data ?? [])
    .flatMap((meal) => meal.items.map((item) => ({ meal, item })))
    .filter(({ item }) => item.state === 'NeedsConfirmation');

  return (
    <>
      {/* El aviso de conexión vive en AppLayout: acá estaba duplicado y sólo acá. */}
      {/*
        La cuenta se alcanza desde acá y no desde la barra de abajo.

        Antes vivía en un enlace al final de «Cuerpo», así que el perfil, los objetivos, la
        exportación de los datos, el tema y cerrar sesión estaban escondidos dentro de la pantalla
        de mediciones — que no tiene nada que ver. A la barra tampoco va: son cuatro destinos más
        la cámara, elegidos así a propósito, y un quinto los apretaría a todos por algo que se toca
        una vez por semana.

        Arriba a la izquierda es donde se la busca, y además es la única esquina libre: a la
        derecha están las flechas del día.
      */}
      <header className="row-between">
        <div className="row" style={{ gap: 'var(--space-3)' }}>
          <Link to="/cuenta" className="cuenta-atajo" aria-label="Tu cuenta">
            <AccountIcon size={19} />
          </Link>
          <div>
            <Eyebrow>{longDate.format(new Date(`${date}T12:00:00`))}</Eyebrow>
            <h1>{describeDay(date)}</h1>
          </div>
        </div>
        <div className="row">
          <IconButton label="Día anterior" onClick={() => setDate(shiftIsoDate(date, -1))}>
            <BackIcon size={18} />
          </IconButton>
          <IconButton
            label="Día siguiente"
            disabled={isToday}
            onClick={() => setDate(shiftIsoDate(date, 1))}
          >
            <ForwardIcon size={18} />
          </IconButton>
        </div>
      </header>

      {profile !== null && !profile.isComplete && (
        <Banner tone="decision">
          Te faltan tres datos del perfil para poder fijar un objetivo.{' '}
          <Link to="/perfil">Completarlo</Link>
        </Banner>
      )}

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {summary.error !== null && <Banner tone="danger">{summary.error}</Banner>}

      {/*
        El esqueleto sólo cuando no hay nada que mostrar. Al confirmar un ítem el día se vuelve
        a leer, pero los datos anteriores siguen siendo válidos: vaciar la pantalla para
        repintar lo mismo un instante después es el parpadeo que esto viene a sacar.
      */}
      {esqueletoDelDia && summary.data === null && (
        <>
          <EsqueletoTotalDelDia />
          <EsqueletoComidas />
        </>
      )}

      {summary.data !== null && (
        <div className="stack appear">
          <DayTotalCard
            summary={summary.data}
            onShowExcluded={() =>
              excludedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          />

          {/*
            El aviso de confirmación queda a un toque del resumen: el ítem que espera es
            justamente el que hace que el total sea un rango, así que la salida tiene que estar
            donde se lee el problema.
          */}
          {needsConfirmation.length > 0 && needsConfirmation[0] !== undefined && (
            <ConfirmationNotice
              count={needsConfirmation.length}
              item={needsConfirmation[0].item}
              mealLabel={mealTypeLabel(needsConfirmation[0].meal.type)}
              onOpen={() =>
                navigate(
                  `/comidas/${needsConfirmation[0]?.meal.id}/items/${needsConfirmation[0]?.item.id}`,
                )
              }
            />
          )}

          {meals.data !== null && meals.data.length === 0 ? (
            <EmptyDay
              onCapture={() => navigate('/capturar')}
              onWrite={() => navigate('/registrar')}
              onQuickDay={() => navigate('/dia')}
            />
          ) : (
            <section className="stack">
              <div className="row-between">
                <h2>Comidas</h2>
                <span className="micro muted">{countLabel(meals.data ?? [])}</span>
              </div>

              {(meals.data ?? []).map((meal) => {
                const breakdown = summary.data?.meals.find((entry) => entry.mealId === meal.id);
                const lowConfidence = meal.items.find((item) => item.state === 'NeedsConfirmation');

                return (
                  <MealCard
                    key={meal.id}
                    meal={meal}
                    totalLow={breakdown?.totals.kcalLow}
                    totalHigh={breakdown?.totals.kcalHigh}
                    onOpenItem={(itemId) => navigate(`/comidas/${meal.id}/items/${itemId}`)}
                    onOpenMeal={() => navigate(`/comidas/${meal.id}`)}
                    footer={
                      lowConfidence?.currentEstimate == null ? undefined : (
                        <div className="row" style={{ padding: '10px 16px 14px', gap: 8 }}>
                          <Button
                            variant="primary"
                            style={{ flex: 1.6, minHeight: 'var(--touch-target)' }}
                            onClick={() => void confirm(meal.id, lowConfidence.id)}
                          >
                            {/*
                              Sin número. Acá decía «Confirmar 620 kcal», que es el valor puntual
                              de una estimación con confianza menor a 0,60 — exactamente lo que
                              RF-24 prohíbe presentar. Que el número sea el resultado de confirmar
                              y no la lectura actual no cambia lo que la persona lee: un número
                              redondo, en el control más prominente de la fila.

                              No se pierde nada: la banda está en la fila de arriba, y el punto
                              aparece apenas se confirma, que es cuando pasa a ser legítimo.
                            */}
                            Confirmar la estimación
                          </Button>
                          <Button
                            variant="quiet"
                            style={{ flex: 1, minHeight: 'var(--touch-target)' }}
                            onClick={() =>
                              navigate(`/comidas/${meal.id}/items/${lowConfidence.id}`)
                            }
                          >
                            Corregir
                          </Button>
                        </div>
                      )
                    }
                  />
                );
              })}

              {/*
                La misma puerta que el día vacío, para el día que ya empezó. El botón de la barra
                lleva a la cámara, que es el camino frecuente; anotar algo que no se ve —un café,
                una fruta— no tenía ninguno.
              */}
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <Button variant="quiet" style={{ flex: 1 }} onClick={() => navigate('/registrar')}>
                  Anotar sin foto
                </Button>
                <Button variant="quiet" style={{ flex: 1 }} onClick={() => navigate('/dia')}>
                  Cargar el día
                </Button>
              </div>
            </section>
          )}

          <div ref={excludedRef}>
            <ExcludedList items={summary.data.excluded} />
          </div>
        </div>
      )}
    </>
  );
}

function countLabel(meals: Meal[]): string {
  const items = meals.reduce((total, meal) => total + meal.items.length, 0);
  const mealWord = meals.length === 1 ? 'comida' : 'comidas';
  const itemWord = items === 1 ? 'ítem' : 'ítems';

  return `${meals.length} ${mealWord} · ${items} ${itemWord}`;
}

function ConfirmationNotice({
  count,
  item,
  mealLabel,
  onOpen,
}: {
  count: number;
  item: MealItem;
  mealLabel: string;
  onOpen: () => void;
}) {
  const estimate = item.currentEstimate;

  return (
    <button
      type="button"
      className="banner banner-decision"
      onClick={onOpen}
      style={{
        width: '100%',
        border: 'none',
        borderRadius: 'var(--radius-block)',
        textAlign: 'left',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      {estimate !== null && (
        <ConfidenceMeter source={estimate.source} confidence={estimate.confidence} />
      )}
      <span className="stack" style={{ flex: 1, gap: 2 }}>
        <strong className="micro">
          {count === 1
            ? '1 ítem espera que lo confirmes'
            : `${count} ítems esperan que los confirmes`}
        </strong>
        <span className="nano muted">
          {mealLabel} salió de una foto. Un toque y el total se fija.
        </span>
      </span>
      <ForwardIcon size={16} />
    </button>
  );
}

/**
 * El día sin registros.
 *
 * Antes esto era un póster: un ícono de cámara gris y grande, un título centrado y tres botones
 * apilados de anchos distintos. Se leía viejo, y sobre todo se leía como OTRA aplicación — el
 * resto del producto es un extracto de cuenta hecho de filas, y acá aparecía un cartel.
 *
 * Ahora son filas, las mismas del ledger. Cada una dice qué hace y cuándo conviene, porque las
 * tres puertas no son intercambiables: la foto es para el plato que tenés adelante, escribir es
 * para el café que ya te tomaste, y cargar el día es para cuando venís de acordarte de todo
 * junto. Un botón que sólo dice «Anotar sin foto» no te dice cuándo usarlo.
 *
 * Sin ilustración. El día en cero no es un problema que haya que suavizar con un dibujo: es la
 * hora de empezar, y lo único que hace falta es que las puertas estén a la mano.
 */
function EmptyDay({
  onCapture,
  onWrite,
  onQuickDay,
}: {
  onCapture: () => void;
  onWrite: () => void;
  onQuickDay: () => void;
}) {
  const puertas = [
    {
      icono: <CameraIcon size={20} />,
      titulo: 'Sacar la foto del plato',
      detalle: 'Lo estimamos por vos',
      accion: onCapture,
      principal: true,
    },
    {
      icono: <EditIcon size={20} />,
      titulo: 'Anotar sin foto',
      detalle: 'Un café, una fruta, algo que sabés decir',
      accion: onWrite,
      principal: false,
    },
    {
      icono: <TodayIcon size={20} />,
      titulo: 'Cargar el día completo',
      detalle: 'Desayuno, almuerzo, merienda y cena de una',
      accion: onQuickDay,
      principal: false,
    },
  ];

  return (
    <section className="stack">
      <p className="small muted" style={{ margin: 0 }}>
        El día arranca en cero y eso no dice nada de vos. Por dónde empezás:
      </p>

      <div className="card">
        {puertas.map((puerta) => (
          <button
            key={puerta.titulo}
            type="button"
            className={puerta.principal ? 'ledger-row ledger-row-decision' : 'ledger-row'}
            onClick={puerta.accion}
          >
            <span className="empty-gate-icon" aria-hidden="true">
              {puerta.icono}
            </span>
            <span className="ledger-main">
              <span className="ledger-name">{puerta.titulo}</span>
              <span className="ledger-meta">{puerta.detalle}</span>
            </span>
            <ForwardIcon size={16} />
          </button>
        ))}
      </div>

      {/*
        La cuarta puerta va aparte y en voz baja: sólo sirve si ya registraste antes, así que en
        la primera vez que alguien ve esta pantalla no aplica.
      */}
      <Link to="/historial" className="small" style={{ alignSelf: 'flex-start' }}>
        Duplicar una comida de ayer
      </Link>
    </section>
  );
}
